import { CombinedGraphQLErrors } from '@apollo/client'
import { DEFAULT_THEME, Theme } from '@/types/theme'
import client from '@/apolloClient'
import { GET_CHANNEL_BY_DOMAIN_QUERY } from '@/api/getChannelByDomain'
import { GET_FREE_SHIPPING_RATES_QUERY } from '@/api/getFreeShippingRates'
import { VENDURE_CHANNEL_TOKEN_HEADER_KEY, VENDURE_LANGUAGE_CODE_HEADER_KEY } from '@/config'
import Cookies from 'js-cookie'
import type { FreeShippingRate } from '@/store/channel'

function hasChannelNotFoundError(error: unknown): boolean {
  if (!CombinedGraphQLErrors.is(error)) {
    return false
  }

  return error.errors.some((graphqlError) => graphqlError.extensions?.code === 'CHANNEL_NOT_FOUND')
}

export type LoadChannelResult =
  | {
      status: 'available'
      theme: Theme
      channelId: string
      shopIsPublic: boolean
      googleTagManagerId?: string | null
      availableLanguageCodes: string[]
      defaultLanguageCode: string | null | undefined
      deliveryDays: number | null | undefined
      pricesIncludeTax: boolean
      freeShippingRates: FreeShippingRate[]
    }
  | {
      status: 'unavailable'
    }

export async function loadTenantTheme(domain: string | null): Promise<LoadChannelResult> {
  if (!domain) {
    return { status: 'unavailable' }
  }

  try {
    const { data, error } = await client.query({
      query: GET_CHANNEL_BY_DOMAIN_QUERY,
      variables: { domain },
      errorPolicy: 'all',
    })

    if (hasChannelNotFoundError(error)) {
      Cookies.remove(VENDURE_CHANNEL_TOKEN_HEADER_KEY)
    }

    const channel = data?.channelByDomain
    if (!channel) {
      return { status: 'unavailable' }
    }

    Cookies.set(VENDURE_CHANNEL_TOKEN_HEADER_KEY, channel.token || '')
    if (!Cookies.get(VENDURE_LANGUAGE_CODE_HEADER_KEY) && channel.defaultLanguageCode) {
      Cookies.set(VENDURE_LANGUAGE_CODE_HEADER_KEY, channel.defaultLanguageCode)
    }

    const customFields = channel.customFields
    const theme: Theme = {
      name: customFields?.storeDisplayName || channel.code,
      colors: {
        primary: customFields?.colors?.primary || DEFAULT_THEME.colors.primary,
        secondary: customFields?.colors?.secondary || DEFAULT_THEME.colors.secondary,
        background: customFields?.colors?.background || DEFAULT_THEME.colors.background,
        foreground: customFields?.colors?.foreground || DEFAULT_THEME.colors.foreground,
      },
      font: {
        family: customFields?.fontFamily || DEFAULT_THEME.font.family,
      },
      logoUrl: customFields?.logo?.preview || DEFAULT_THEME.logoUrl,
      faviconUrl: customFields?.favicon?.preview || DEFAULT_THEME.faviconUrl,
      pwaIcon192Url: customFields?.pwaIcon192?.preview || DEFAULT_THEME.pwaIcon192Url,
      pwaIcon512Url: customFields?.pwaIcon512?.preview || DEFAULT_THEME.pwaIcon512Url,
    }

    const shopIsPublic = customFields?.shopIsPublic ?? false
    const googleTagManagerId = customFields?.googleTagManagerId
    const deliveryDays = customFields?.deliveryDays
    const pricesIncludeTax = channel.pricesIncludeTax ?? true

    // Requires the vendure-token cookie set above, since freeShippingRates is
    // resolved from the request's channel context, not a `domain` argument.
    let freeShippingRates: FreeShippingRate[] = []
    try {
      const { data: freeShippingData } = await client.query({
        query: GET_FREE_SHIPPING_RATES_QUERY,
        errorPolicy: 'all',
      })
      freeShippingRates = freeShippingData?.freeShippingRates ?? []
    } catch (err) {
      console.error(`${loadTenantTheme.name} Failed to load free shipping rates.`, err)
    }

    return {
      status: 'available',
      theme,
      channelId: channel.id,
      shopIsPublic,
      googleTagManagerId,
      availableLanguageCodes: channel.availableLanguageCodes ?? [],
      defaultLanguageCode: channel.defaultLanguageCode,
      deliveryDays,
      pricesIncludeTax,
      freeShippingRates,
    }
  } catch (err) {
    console.error(`${loadTenantTheme.name} Failed to load channel for domain "${domain}".`, err)
    return { status: 'unavailable' }
  }
}
